import { filledArrow } from '../renderers';

import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';

const DEFAULT_TOP_H = 7;
const TOP_THICK = 2;
const EXTRA_W_FOR_BAR = 2;
const ARROW_HEIGHT = 5;
// Future selves: investigate potential discrepancy with ARROW_HEIGHT
const ARROW_SIZE = 4;
const VERT_HAT_SHIFT = 4;

export class Barred extends MathObject {
  multi: Multi;
  line: Line;
  lineType: any;

  topH = DEFAULT_TOP_H;

  constructor(line: Line, multi: Multi) {
    super();

    this.t = 'barred';
    this.gmmName = 'barred';

    this.multi = multi;
    this.line = line;

    this.children.push(this.multi);
    this.multi.parent = this;
  }

  getPaintedCharCount() {
    return this.multi.getPaintedCharCount();
  }

  setEditable(b: boolean): void {
    this.multi.setEditable(b);
  }

  setFont(f: string): void {
    this.multi.setFont(f);
  }

  setStuff(stuff: Multi): void {
    this.children = [];
    this.children.push(stuff);
    this.multi = stuff;
    stuff.parent = this;
  }

  setLineType(lineType: any): void {
    this.lineType = lineType;

    // Add height for the arrows
    if (lineType.toLowerCase() != 'line') {
      this.topH += ARROW_HEIGHT;
    }
  }

  setLine(line: Line): void {
    this.line = line;
    this.multi.setLine(line);
  }

  toXML(): string {
    return '<b>' + this.multi.toXML() + '</b>';
  }

  swapString(old: string, replacer: string): void {
    this.multi.swapString(old, replacer);
  }

  grabFocus(direction: Direction): void {
    this.multi.grabFocus(direction);
  }

  buildSizeRecursive(): void {
    this.multi.buildSizeRecursive();
    this.buildSize();
  }

  buildSize(): void {
    let w = this.multi.viewportW;

    w += EXTRA_W_FOR_BAR * 2;
    let h = this.multi.viewportH;

    h += this.topH;
    this.setAllDim(w, h);
    this.setPositionsOfChildren();
  }

  getVertToBaseline(): number {
    return this.multi.getVertToBaseline() + this.topH;
  }

  setPositionsOfChildren(): void {
    this.multi.viewportX = EXTRA_W_FOR_BAR;
    this.multi.viewportY = this.topH;
  }

  childSizeChanged(): void {
    this.buildSize();
    (this.parent as MathObject)?.childSizeChanged();
  }

  paintMe(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.strokeStyle = this.fillStyle || 'black';
    ctx.fillStyle = this.fillStyle || 'black';
    let rightArrow = false;
    let leftArrow = false;

    if (this.lineType != null) {
      rightArrow =
        this.lineType.toLowerCase() === 'arrow_right' ||
        this.lineType.toLowerCase() === 'arrow_both';
      leftArrow =
        this.lineType.toLowerCase() === 'arrow_left' ||
        this.lineType.toLowerCase() === 'arrow_both';
    }

    let xLineStart = 0;
    let xLineEnd = this.viewportW;
    const yValue = this.topH / 2;

    if (this.lineType == 'ARC') {
      ctx.beginPath();
      ctx.lineWidth = TOP_THICK;
      ctx.ellipse(
        (xLineEnd + xLineStart) / 2,
        yValue + ARROW_SIZE,
        (xLineEnd - xLineStart) / 2,
        ARROW_SIZE,
        0,
        0,
        Math.PI,
        true
      );
      ctx.stroke();
      ctx.restore();
    } else if (this.lineType == 'HAT') {
      ctx.beginPath();
      ctx.lineWidth = TOP_THICK;
      // using VERT_HAT_SHIFT to lower "hat"... else "hat" is too high above variable
      ctx.moveTo(xLineStart, yValue + VERT_HAT_SHIFT);
      ctx.lineTo((xLineEnd - xLineStart) / 2, yValue);
      ctx.lineTo(xLineEnd, yValue + VERT_HAT_SHIFT);
      ctx.stroke();
      ctx.restore();
    } else {
      if (rightArrow) {
        xLineEnd = xLineEnd - ARROW_SIZE;
        // Draw Arrow
        filledArrow(ctx, 0, yValue, xLineEnd, yValue, ARROW_SIZE);
      }

      if (leftArrow) {
        xLineStart = ARROW_SIZE;
        // Draw Arrow
        filledArrow(ctx, xLineEnd, yValue, ARROW_SIZE, yValue, ARROW_SIZE);
      }

      // Draw the Line
      ctx.beginPath();
      ctx.lineWidth = TOP_THICK;
      ctx.moveTo(xLineStart, yValue);
      ctx.lineTo(xLineEnd, yValue);
      ctx.stroke();
      ctx.restore();
    }
  }

  add(tile: MathObject): void {
    this.multi.add(tile);
  }
}

export function buildBarred(
  problemJS: MultiDependencies,
  line: Line,
  stuff?: Multi
): Barred {
  return new Barred(line, stuff || buildDefaultMulti(problemJS, line));
}
